import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '../../../base/dialog/actions';
import { IconModerator } from '../../../base/icons/svg';
import { PARTICIPANT_ROLE } from '../../../base/participants/constants';
import { getLocalParticipant, getParticipantById, isParticipantModerator } from '../../../base/participants/functions';
import ContextMenuItem from '../../../base/ui/components/web/ContextMenuItem';
import { NOTIFY_CLICK_MODE } from '../../../toolbox/types';
import GrantModeratorDialog from './GrantModeratorDialog';
/**
 * Belltroop "Grant / Revoke Co-host" toggle in the participant menu.
 *
 * Behavior:
 *  - Hidden if the local participant is not the (authenticated) admin or if
 *    the target is already an XMPP moderator (the "Revoke Mod" affordance
 *    for legacy XMPP moderators lives in the popout window).
 *  - When the target is NOT yet a co-host, shows "Grant Co-host" and opens
 *    the existing confirmation dialog. Confirming dispatches GRANT_MODERATOR
 *    which is intercepted in base/participants/middleware.ts and routed to
 *    `window.belltroopGrantCohost`.
 *  - When the target IS a co-host, shows "Revoke Co-host" and calls
 *    `window.belltroopRevokeCohost` directly (no confirmation dialog —
 *    revoking is an immediate, low-risk action).
 *
 * The `isCohost` flag on the participant entry is set by plugin.head.html's
 * applyCohostListFromJson() when admin's belltroop-cohosts presence property
 * changes.
 *
 * @returns {JSX.Element|null}
 */
const GrantModeratorButton = ({ notifyClick, notifyMode, participantID }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const localParticipant = useSelector(getLocalParticipant);
    const targetParticipant = useSelector((state) => getParticipantById(state, participantID));
    const targetIsCohost = Boolean(targetParticipant?.isCohost);
    const visible = useMemo(() => Boolean(localParticipant?.role === PARTICIPANT_ROLE.MODERATOR)
        && !isParticipantModerator(targetParticipant), [isParticipantModerator, localParticipant, targetParticipant]);
    const handleClick = useCallback(() => {
        notifyClick?.();
        if (notifyMode === NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY) {
            return;
        }
        if (targetIsCohost) {
            const fn = window.belltroopRevokeCohost;
            if (typeof fn === 'function') {
                fn(participantID);
            }
            return;
        }
        dispatch(openDialog('GrantModeratorDialog', GrantModeratorDialog, { participantID }));
    }, [dispatch, notifyClick, notifyMode, participantID, targetIsCohost]);
    if (!visible) {
        return null;
    }
    return (React.createElement(ContextMenuItem, { accessibilityLabel: t(targetIsCohost
            ? 'toolbar.accessibilityLabel.revokeCohost'
            : 'toolbar.accessibilityLabel.grantModerator'), className: 'grantmoderatorlink', icon: IconModerator, onClick: handleClick, text: t(targetIsCohost
            ? 'videothumbnail.revokeCohost'
            : 'videothumbnail.grantModerator') }));
};
export default GrantModeratorButton;
